import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalData, CandidateApplication } from '../interfaces/candidate.interface';
import { DateService } from '../services/date.service';

@Component({
  selector: 'app-action-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  // Dans le template, remplacer la partie modal-body par :
  template: `
    <div class="modal-overlay" (click)="onOverlayClick($event)">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3>{{ getModalTitle() }}</h3>
          <button class="close-btn" (click)="onClose()">×</button>
        </div>
        
        <div class="modal-body">
          <div *ngIf="modalData?.action === 'proposed-options'" class="options-section">
            <p class="instruction">{{ getInstruction() }}</p>
            <div class="date-info cdi">
              <strong>Date concernée :</strong> {{ formatDate(modalData.dates[0]) }}
            </div>
            <div class="action-buttons abf">
              <button class="btn btn-success action-option" (click)="selectProposedOption('accept')">
                Accepter cette candidature
              </button>
              <button class="btn btn-danger action-option" (click)="selectProposedOption('reject')">
                Refuser cette candidature
              </button>
            </div>
          </div>
          
          <div *ngIf="!showConfirmation && modalData?.action !== 'proposed-options'" class="date-selection">
            <p class="instruction">{{ getInstruction() }}</p>
            
            <div class="select-all-section">
              <button 
                class="select-all-btn"
                (click)="selectAllDates()">
                Sélectionnez toutes les dates
              </button>
            </div>
            
            <div class="dates-list">
              <div *ngFor="let date of availableDates" class="date-item">
                <label class="date-checkbox">
                  <input 
                    type="checkbox"
                    [(ngModel)]="selectedDates[date]"
                    [value]="date">
                  <span class="checkmark"></span>
                  {{ formatDate(date) }}
                </label>
              </div>
            </div>
          </div>
          
          <div *ngIf="showConfirmation" class="confirmation-section">
            <div class="confirmation-message">
              <p>{{ getConfirmationMessage() }}</p>
              <div class="selected-dates-summary">
                <strong>Dates sélectionnées :</strong>
                <ul>
                  <li *ngFor="let date of getSelectedDatesList()">
                    {{ formatDate(date) }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <div *ngIf="!showConfirmation && modalData?.action !== 'proposed-options'" class="initial-actions">
            <button 
              class="btn btn-secondary" 
              (click)="onClose()">
              Annuler
            </button>
            <button 
              class="btn btn-primary"
              [disabled]="getSelectedDatesList().length === 0"
              (click)="showConfirmationStep()">
              Continuer
            </button>
          </div>
          
          <div *ngIf="showConfirmation" class="confirmation-actions">
            <button 
              class="btn btn-secondary" 
              (click)="backToSelection()">
              Retour
            </button>
            <button 
              class="btn btn-danger" 
              (click)="confirmAction()">
              OUI
            </button>
            <button 
              class="btn btn-secondary" 
              (click)="onClose()">
              NON
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .cdi {
      margin-bottom: 18px;
    }

    .abf {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }

    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .modal-content {
      background: white;
      border-radius: 8px;
      width: 90%;
      max-width: 600px;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border-bottom: 1px solid #eee;
    }

    .modal-header h3 {
      margin: 0;
      color: #333;
      font-size: 18px;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #999;
      padding: 0;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .close-btn:hover {
      color: #333;
    }

    .modal-body {
      padding: 20px;
    }

    .instruction {
      margin-bottom: 20px;
      color: #555;
      line-height: 1.5;
    }

    .select-all-section {
      margin-bottom: 20px;
    }

    .select-all-btn {
      background-color: #007bff;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    }

    .select-all-btn:hover {
      background-color: #0056b3;
    }

    .dates-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .date-item {
      display: flex;
      align-items: center;
    }

    .date-checkbox {
      display: flex;
      align-items: center;
      cursor: pointer;
      font-size: 14px;
      user-select: none;
    }

    .date-checkbox input[type="checkbox"] {
      margin-right: 10px;
      transform: scale(1.2);
    }

    .confirmation-section {
      text-align: center;
    }

    .confirmation-message {
      margin-bottom: 20px;
    }

    .confirmation-message p {
      font-size: 16px;
      color: #333;
      margin-bottom: 15px;
    }

    .selected-dates-summary {
      text-align: left;
      background-color: #f8f9fa;
      padding: 15px;
      border-radius: 4px;
      border-left: 4px solid #007bff;
    }

    .selected-dates-summary ul {
      margin: 10px 0 0 0;
      padding-left: 20px;
    }

    .selected-dates-summary li {
      margin-bottom: 5px;
    }

    .modal-footer {
      padding: 20px;
      border-top: 1px solid #eee;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }

    .initial-actions,
    .confirmation-actions {
      display: flex;
      gap: 10px;
    }

    .btn {
      padding: 8px 16px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.2s ease;
    }

    .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-primary {
      background-color: #007bff;
      color: white;
    }

    .btn-primary:hover:not(:disabled) {
      background-color: #0056b3;
    }

    .btn-secondary {
      background-color: #6c757d;
      color: white;
    }

    .btn-secondary:hover {
      background-color: #545b62;
    }

    .btn-danger {
      background-color: #dc3545;
      color: white;
    }

    .btn-danger:hover {
      background-color: #c82333;
    }
  `]
})
export class ActionModalComponent implements OnInit {
  @Input() modalData!: ModalData;
  @Input() isVisible: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<string[]>();

  selectedDates: { [date: string]: boolean } = {};
  showConfirmation: boolean = false;
  availableDates: string[] = [];

  ngOnInit() {
    this.initializeModal();
  }

  ngOnChanges() {
    if (this.isVisible) {
      this.initializeModal();
    }
  }

  initializeModal() {
    this.showConfirmation = false;
    this.selectedDates = {};
    this.availableDates = this.getAvailableDates();
  }

  getAvailableDates(): string[] {
    if (!this.modalData) return [];
    
    const relevantApplications = this.modalData.candidate.applications.filter(app => {
      switch (this.modalData.action) {
        case 'accept':
        case 'reject':
          return app.status === 'PROPOSED';
        case 'cancel-accepted':
          return app.status === 'ACCEPTED';
        case 'cancel-signed':
          return app.status === 'SIGNED';
        default:
          return false;
      }
    });

    // Generate all dates from the relevant applications
    const dates: string[] = [];
    relevantApplications.forEach(app => {
      const appDates = DateService.generateDateRange(app.fromDate, app.toDate);
      dates.push(...appDates);
    });

    // Remove duplicates and sort
    return [...new Set(dates)].sort();
  }

  // Dans la méthode getModalTitle
  getModalTitle(): string {
    if (!this.modalData) return '';
    
    const actionTitles = {
      'accept': 'Accepter la candidature',
      'reject': 'Refuser la candidature',
      'cancel-accepted': 'Annuler les dates acceptées',
      'cancel-signed': 'Annuler les dates affectées signées',
      'proposed-options': 'Actions sur la candidature proposée'
    };
    
    return actionTitles[this.modalData.action] || '';
  }

  // Dans la méthode getInstruction
  getInstruction(): string {
    if (!this.modalData) return '';
    
    const instructions = {
      'accept': 'Sélectionnez les dates que vous souhaitez accepter pour ce candidat :',
      'reject': 'Sélectionnez les dates que vous souhaitez refuser pour ce candidat :',
      'cancel-accepted': 'Sélectionnez les dates acceptées que vous souhaitez annuler :',
      'cancel-signed': 'Sélectionnez les dates signées que vous souhaitez annuler :',
      'proposed-options': 'Choisissez l\'action à effectuer sur cette candidature :'
    };
    
    return instructions[this.modalData.action] || '';
  }

  getConfirmationMessage(): string {
    if (!this.modalData) return '';
    
    const messages : any = {
      'accept': 'Êtes-vous sûr(e) de vouloir accepter cette candidature ?',
      'reject': 'Êtes-vous sûr(e) de vouloir refuser cette candidature ?',
      'cancel-accepted': 'Êtes-vous sûr(e) de vouloir annuler ces dates ?',
      'cancel-signed': 'Êtes-vous sûr(e) de vouloir annuler ces dates ?'
    };
    
    return messages[this.modalData.action] || '';
  }

  selectAllDates(): void {
    this.availableDates.forEach(date => {
      this.selectedDates[date] = true;
    });
  }

  getSelectedDatesList(): string[] {
    return Object.keys(this.selectedDates).filter(date => this.selectedDates[date]);
  }

  showConfirmationStep(): void {
    this.showConfirmation = true;
  }

  backToSelection(): void {
    this.showConfirmation = false;
  }

  confirmAction(): void {
    const selectedDatesList = this.getSelectedDatesList();
    this.confirm.emit(selectedDatesList);
    this.onClose();
  }

  onClose(): void {
    this.showConfirmation = false;
    this.selectedDates = {};
    this.close.emit();
  }

  onOverlayClick(event: Event): void {
    this.onClose();
  }

  formatDate(dateStr: string): string {
    return DateService.formatDate(dateStr);
  }
  
  // Dans la classe ActionModalComponent
  selectProposedOption(option: 'accept' | 'reject'): void {
  // Émettre l'événement avec l'option sélectionnée et la date
  if (this.modalData && this.modalData.dates && this.modalData.dates.length > 0) {
  // Créer un objet avec l'action choisie et la date
  const result = {
  action: option,
  dates: this.modalData.dates
  };
  
  // Émettre l'objet result au lieu de juste les dates
  this.confirm.emit([JSON.stringify(result)]);
  
  this.onClose();
  }
}

}